/**
 * ErrorBoundary — class-based React error boundary.
 *
 * Catches render errors in child components and displays
 * a fallback UI. Reports errors to GlobalErrorService for
 * structured logging and monitoring.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { globalErrorService } from '@/lib';

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    globalErrorService.captureFromErrorBoundary(error, {
      componentStack: errorInfo.componentStack,
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center gap-4 p-8"
        >
          <div className="flex flex-col items-center gap-2 text-center">
            <p className="text-xl font-semibold text-foreground">Application Error</p>
            <p className="text-sm text-muted-foreground max-w-md">
              An unexpected error occurred while rendering this page. Please refresh the
              application to continue.
            </p>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Refresh
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
